import { Button, message } from 'antd'
import { getOperationName } from 'apollo-link'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  GetTimelinePostsDocument,
  SectionType,
  useGetGroupQuery,
  usePostCreateMutation,
  usePostShareMutation,
  usePostUpdateMutation,
} from '@/generated/graphql'

import { PostEditorSetState, PostEditorState, emptyPostEditorState } from './post-editor.state'

interface PostEditorPublishProps {
  postId?: number
  sectionId?: SectionType
  groupId?: number
  isShare?: boolean
  state: PostEditorState
  setState: PostEditorSetState
  onSubmit: () => void
}

export const PostEditorPublish: React.FC<PostEditorPublishProps> = ({
  postId,
  sectionId,
  groupId,
  isShare,
  state,
  setState,
  onSubmit,
}) => {
  const [t] = useTranslation()
  const { data: groupData } = useGetGroupQuery({
    skip: !groupId,
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    variables: {
      groupId: groupId!,
      withAdmins: false,
      pagination: {},
    },
  })

  const group = groupData?.group

  const cleanState = () => {
    setState((state) => ({
      ...state,
      ...emptyPostEditorState,
      isMarkdown: state!.isMarkdown,
    }))
  }

  const onComplete = () => {
    cleanState()
    onSubmit()
  }

  const [createPost, { loading: createLoading }] = usePostCreateMutation({
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
    onCompleted(data) {
      if (!data) {
        return
      }
      onComplete()
    },
  })

  const [updatePost, { loading: updateLoading }] = usePostUpdateMutation({
    onCompleted: onSubmit,
  })

  const [sharePost, { loading: shareLoading }] = usePostShareMutation({
    onCompleted(data) {
      if (!data) {
        return
      }
      message.success(t('post.shareSuccess') + ' ' + (group ? group.name : t('post.editor.myTimeline')))
      onComplete()
    },
  })

  const onClickPublish = () => {
    const post = {
      isMarkdown: state.isMarkdown,
      isAnonymous: state.isAnonymous,
      content: state.content || '',
      links: Object.values(state.links).map(({ title, description, logo, url }) => ({ title, description, logo, url })),
      fileIds: state.files.map(({ fileId }) => fileId),
    }

    if (isShare) {
      sharePost({
        variables: {
          post: {
            ...post,
            originalPostId: postId!,
            groupId,
          },
        },
      })
      return
    }

    if (postId) {
      updatePost({
        variables: {
          postId,
          post,
        },
      })
      return
    }

    createPost({
      variables: {
        post: {
          ...post,
          sectionId,
          groupId,
          postType: state.postType,
        },
      },
    })
  }

  const notDisabled = isShare
    ? true
    : state.content.trim().length || state.files.length || Object.keys(state.links).length

  const buttonProps = {
    type: 'primary' as const,
    size: 'middle' as const,
    disabled: !notDisabled,
    onClick: () => onClickPublish(),
  }

  const buttonText = isShare ? t('post.editor.share') : postId ? t('post.editor.save') : t('post.editor.publishTo')

  return (
    <Button loading={createLoading || updateLoading || shareLoading} {...buttonProps}>
      {buttonText}
    </Button>
  )
}
